import {
  isDeviceMasterQuoteField,
  isQuoteDefinitionBound,
  permissionFieldIdForDeviceProperty,
} from './businessFieldDirectory';
import {
  isValidCustomFieldValue,
  ownerForCustomDefinition,
} from './customFieldValues';
import {
  activeActorPermissionContext,
  activeRoleIdForState,
  createDisposableFieldGrantResolver,
  quotePermissionContext,
  resolveDisposableFieldGrant,
  resolveDisposablePermissionAction,
} from './permissionPolicy';
import {
  isQuoteFieldAvailable,
  quoteFieldAvailabilityForQuote,
} from './quoteFieldAvailability';
import type {
  BusinessCatalog,
  CpqRole,
  CpqState,
  DeviceCatalogRecord,
  Quote,
  QuoteColumnSnapshot,
  QuoteFieldDefinitionRecord,
} from './types';
import { unifiedFieldBindingForKey } from './unifiedFieldRegistry';

export type DisposableFieldPermissionContext =
  | Pick<CpqState, 'catalog' | 'role' | 'currentRoleId' | 'currentActorUserId'>
  | Pick<BusinessCatalog, 'permissionGovernance' | 'roles' | 'users'>;

export type QuoteFieldAuthorization = (
  fieldKey: string,
  mode: 'view' | 'edit',
) => boolean;

function permissionState(context: DisposableFieldPermissionContext) {
  return 'catalog' in context
    ? context
    : { catalog: context as BusinessCatalog };
}

function disposableFieldGrantAllowed(
  role: CpqRole,
  key: string,
  action: 'view' | 'edit',
  quote: Quote,
  context?: DisposableFieldPermissionContext,
  resolveFieldGrant?: ReturnType<typeof createDisposableFieldGrantResolver>,
): boolean {
  // A field consumer without an explicit persisted member and resource is not
  // authorized. Legacy helpers must not reconstruct access from a role key.
  if (!context || !('currentActorUserId' in context)) return false;
  const normalized = permissionState(context);
  const roleId =
    'currentRoleId' in normalized
      ? activeRoleIdForState(normalized)
      : undefined;
  if (
    !roleId ||
    normalized.catalog.roles.find((item) => item.id === roleId)?.key !== role
  )
    return false;
  if (
    !resolveDisposablePermissionAction(
      normalized,
      roleId,
      'quotes',
      'quotes:quote.read',
      quote.status === 'draft' ? 'draft' : 'submitted',
      quotePermissionContext(normalized, quote),
    )
  )
    return false;
  return (
    resolveFieldGrant ??
    ((...args) => resolveDisposableFieldGrant(normalized, ...args))
  )(
    roleId,
    `definition:${key}`,
    action,
    quote.status === 'draft' ? 'draft' : 'submitted',
    quotePermissionContext(normalized, quote),
  );
}

export function hasSafeDefinitionVersion(
  field: QuoteFieldDefinitionRecord,
): boolean {
  const version = field.governance.definitionVersion;
  return version === 'definition-v1' || version === 'definition-v2';
}

/**
 * Temporary client-side mapping of the approved 4.1 RBAC field policies.
 * It is a Mock projection only: server-side RBAC remains the security boundary.
 */
export type QuoteFieldPolicy = 'price_sale' | 'price_full' | 'cost_full';

const QUOTE_FIELD_POLICY_BY_KEY: Record<string, QuoteFieldPolicy> = {
  jky_code: 'price_sale',
  rental_price: 'price_sale',
  tax_inclusive_plan_price: 'price_sale',
  device_unit_price: 'price_sale',
  device_untaxed_plan_price: 'price_sale',
  device_compensation_price: 'price_sale',
  rental_subtotal: 'price_sale',
  unit_price_subtotal: 'price_sale',
  scheme_discount: 'price_sale',
  item_discount: 'price_sale',
  product_final_discount_price: 'price_sale',
  package_item_price: 'price_full',
  package_item_monthly_subtotal: 'price_full',
  scheme_product_price: 'price_full',
  scheme_product_subtotal: 'price_full',
  package_discount_quote_tax13: 'price_full',
  package_discounted_unit_price: 'price_full',
  package_discounted_subtotal: 'price_full',
  retail_price_tax13: 'price_sale',
  purchase_final_price_tax13: 'price_full',
  purchase_final_price_ex_tax13: 'price_full',
  dynamic_cost: 'cost_full',
  dynamic_cost_subtotal: 'cost_full',
  device_image: 'price_sale',
  device_name: 'price_sale',
  device_sku: 'price_sale',
  quantity: 'price_sale',
  warehouse_stock: 'price_sale',
  replacement_device: 'price_sale',
  device_category: 'price_sale',
  device_attribute: 'price_sale',
  procurement_attribute: 'price_sale',
  device_weight: 'price_sale',
  quote_remark: 'price_sale',
  quote_title: 'price_sale',
  customer_name: 'price_sale',
  customer_contact: 'price_sale',
  customer_phone: 'price_sale',
  valid_until: 'price_sale',
  business_mode: 'price_sale',
  deposit: 'price_sale',
  overall_discount: 'price_sale',
};

const CATALOG_FIELD_POLICY_BY_KEY: Partial<
  Record<keyof DeviceCatalogRecord, QuoteFieldPolicy>
> = {
  jkyCode: 'price_sale',
  procurementAttribute: 'price_sale',
  weight: 'price_sale',
  rentalPrice: 'price_sale',
  taxInclusivePlanPrice: 'price_sale',
  unitPrice: 'price_sale',
  untaxedPlanPrice: 'price_sale',
  retailPriceTax13: 'price_sale',
  dynamicCost: 'cost_full',
  compensationPrice: 'price_sale',
};

export function quoteFieldPolicyForKey(
  key: string,
): QuoteFieldPolicy | undefined {
  return QUOTE_FIELD_POLICY_BY_KEY[key];
}

/**
 * This Mock state has no actor identity, quote owner, or department binding.
 * Therefore department/self scoped quote reads intentionally fail closed.
 */
export function canProjectQuoteField(
  _role: CpqRole,
  _quote: Quote,
  key: string,
): boolean {
  return isQuoteFieldAvailable(key);
}

/** Definition-aware Disposable projection; custom ordinary fields use the
 * proven all-scope quote read capability, while system fields retain 4.1 maps. */
export function canProjectGovernedQuoteField(
  role: CpqRole,
  quote: Quote,
  key: string,
  definitions: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  authorize?: QuoteFieldAuthorization,
): boolean {
  return canProjectGovernedField(
    role,
    quote,
    key,
    definitions,
    permissionContext,
    undefined,
    authorize,
  );
}

function canProjectGovernedField(
  role: CpqRole,
  quote: Quote,
  key: string,
  definitions: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  resolveFieldGrant?: ReturnType<typeof createDisposableFieldGrantResolver>,
  authorize?: QuoteFieldAuthorization,
): boolean {
  const field = definitions.find((item) => item.key === key);
  if (
    !field?.enabled ||
    field.governance.classification !== 'ordinary' ||
    field.governance.fieldKey !== field.key ||
    !hasSafeDefinitionVersion(field)
  )
    return false;
  if (
    !(authorize
      ? authorize(key, 'view')
      : disposableFieldGrantAllowed(
          role,
          key,
          'view',
          quote,
          permissionContext,
          resolveFieldGrant,
        ))
  )
    return false;
  // A computed quote-workbench field never grants visibility to information
  // that any declared factor would deny. Walk the calculation graph so nested
  // totals inherit their unit-price and base-factor grants as well.
  const factorsAllowed = (
    fieldKey: string,
    visited = new Set<string>(),
  ): boolean => {
    if (visited.has(fieldKey)) return false;
    visited.add(fieldKey);
    const definition = definitions.find((item) => item.key === fieldKey);
    const dependencies =
      definition?.calculation?.operands
        .filter((operand) => operand.kind === 'field')
        .map((operand) => operand.fieldKey)
        .filter((dependency) => dependency !== 'current_pricing_base') ?? [];
    return dependencies.every(
      (dependency) =>
        (authorize
          ? authorize(dependency, 'view')
          : disposableFieldGrantAllowed(
              role,
              dependency,
              'view',
              quote,
              permissionContext,
              resolveFieldGrant,
            )) && factorsAllowed(dependency, new Set(visited)),
    );
  };
  if (!factorsAllowed(key)) return false;
  if (quoteFieldAvailabilityForQuote(key, quote, definitions) !== 'available')
    return false;
  if (field.sourceKind === 'manual') return true;
  return authorize ? true : canProjectQuoteField(role, quote, key);
}

export function canWriteGovernedQuoteField(
  role: CpqRole,
  quote: Quote,
  key: string,
  definitions: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  authorize?: QuoteFieldAuthorization,
): boolean {
  if (
    new Set([
      'scheme_discount',
      'scheme_product_price',
      'scheme_product_subtotal',
      'package_item_price',
      'package_item_monthly_subtotal',
      'package_discount_quote_tax13',
    ]).has(key)
  )
    return false;
  const field = definitions.find((item) => item.key === key);
  return (
    quote.status === 'draft' &&
    field?.sourceKind === 'manual' &&
    !field.calculation &&
    field.governance.kind === 'custom' &&
    (!authorize || ownerForCustomDefinition(field) === 'quote-line') &&
    canProjectGovernedQuoteField(
      role,
      quote,
      key,
      definitions,
      permissionContext,
      authorize,
    ) &&
    (authorize
      ? authorize(key, 'edit')
      : disposableFieldGrantAllowed(
          role,
          key,
          'edit',
          quote,
          permissionContext,
        ))
  );
}

export function canSelectGovernedQuoteField(
  role: CpqRole,
  quote: Quote,
  key: string,
  definitions: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  authorize?: QuoteFieldAuthorization,
): boolean {
  if (
    !canProjectGovernedQuoteField(
      role,
      quote,
      key,
      definitions,
      permissionContext,
      authorize,
    )
  ) {
    return false;
  }
  if (
    quote.status !== 'draft' ||
    !permissionContext ||
    (!authorize && !('currentActorUserId' in permissionContext))
  ) {
    return false;
  }
  const normalized = permissionState(permissionContext);
  const inPool = normalized.catalog.quoteFieldPool.some(
    (entry) =>
      entry.fieldKey === key &&
      entry.enabled &&
      isQuoteDefinitionBound(key) &&
      unifiedFieldBindingForKey(normalized.catalog.quoteFieldDefinitions, key)
        ?.poolAllowed &&
      unifiedFieldBindingForKey(normalized.catalog.quoteFieldDefinitions, key)
        ?.quotePlacement === entry.placement,
  );
  if (!inPool) return false;
  if (authorize) return authorize(key, 'edit');
  const roleId =
    'currentRoleId' in normalized
      ? activeRoleIdForState(normalized)
      : undefined;
  return Boolean(
    roleId &&
      normalized.catalog.roles.find((item) => item.id === roleId)?.key ===
        role &&
      resolveDisposablePermissionAction(
        normalized,
        roleId,
        'quotes',
        'quotes:quote.edit',
        'draft',
        quotePermissionContext(normalized, quote),
      ),
  );
}

export function effectiveQuoteFieldDefinitions(
  role: CpqRole,
  quote: Quote,
  fields: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  authorize?: QuoteFieldAuthorization,
): QuoteFieldDefinitionRecord[] {
  const resolveFieldGrant =
    !authorize && permissionContext && 'currentActorUserId' in permissionContext
      ? createDisposableFieldGrantResolver(permissionState(permissionContext))
      : undefined;
  return fields.filter((field) =>
    canProjectGovernedField(
      role,
      quote,
      field.key,
      fields,
      permissionContext,
      resolveFieldGrant,
      authorize,
    ),
  );
}

export function projectQuoteFieldKeys(
  role: CpqRole,
  quote: Quote,
  keys: string[],
  definitions: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  authorize?: QuoteFieldAuthorization,
): string[] {
  const seen = new Set<string>();
  const resolveFieldGrant =
    !authorize && permissionContext && 'currentActorUserId' in permissionContext
      ? createDisposableFieldGrantResolver(permissionState(permissionContext))
      : undefined;
  return keys.filter((key) => {
    if (seen.has(key)) return false;
    seen.add(key);
    return canProjectGovernedField(
      role,
      quote,
      key,
      definitions,
      permissionContext,
      resolveFieldGrant,
      authorize,
    );
  });
}

export function assertQuoteFieldSelectionAllowed(
  role: CpqRole,
  quote: Quote,
  keys: string[],
  definitions: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  authorize?: QuoteFieldAuthorization,
): void {
  if (
    keys.some(
      (key) =>
        !canSelectGovernedQuoteField(
          role,
          quote,
          key,
          definitions,
          permissionContext,
          authorize,
        ),
    )
  ) {
    throw new Error('字段权限不足，未保存本次字段选择');
  }
}

/**
 * Guards every generic quote write as a backstop for callers that do not use
 * the selection action. Existing denied raw facts must be retained, while new
 * denied fields fail before the cloned state is returned.
 */
export function assertQuoteFieldMutationAllowed(
  role: CpqRole,
  current: Quote,
  next: Quote,
  definitions: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  authorize?: QuoteFieldAuthorization,
): void {
  const protectedKey = (key: string) =>
    !canSelectGovernedQuoteField(
      role,
      current,
      key,
      definitions,
      permissionContext,
      authorize,
    );
  const protectedColumns = (quote: Quote) =>
    quote.customColumns.filter(protectedKey);
  const protectedSnapshots = (quote: Quote) =>
    (quote.customColumnSnapshot ?? []).filter((field) =>
      protectedKey(field.key),
    );
  const sameRawFact = (left: unknown, right: unknown) =>
    JSON.stringify(left) === JSON.stringify(right);

  // Keep denied or unavailable raw facts in their original collections and
  // order. Snapshot comparison is deliberately metadata-complete, not key-only.
  const addedOrChangedGoverned =
    next.customColumns.some(
      (key) => !current.customColumns.includes(key) && protectedKey(key),
    ) ||
    (next.customColumnSnapshot ?? []).some((field) => {
      const previous = (current.customColumnSnapshot ?? []).find(
        (item) => item.key === field.key,
      );
      return (
        protectedKey(field.key) &&
        JSON.stringify(previous) !== JSON.stringify(field)
      );
    });
  if (
    addedOrChangedGoverned ||
    !sameRawFact(protectedColumns(current), protectedColumns(next)) ||
    !sameRawFact(protectedSnapshots(current), protectedSnapshots(next))
  ) {
    throw new Error('字段权限不足，未保存本次字段选择');
  }
  const currentItems = new Map(
    current.items.map((item) => [item.itemId, item]),
  );
  for (const item of next.items) {
    const previous = currentItems.get(item.itemId);
    const previousValues = previous?.manualFieldValues ?? {};
    const nextValues = item.manualFieldValues ?? {};
    for (const key of new Set([
      ...Object.keys(previousValues),
      ...Object.keys(nextValues),
    ])) {
      if (
        isDeviceMasterQuoteField(key) &&
        (!sameRawFact(previousValues[key], nextValues[key]) ||
          nextValues[key] !== undefined)
      ) {
        throw new Error('设备主数据字段只能从冻结快照读取');
      }
      if (
        authorize &&
        !sameRawFact(previousValues[key], nextValues[key]) &&
        nextValues[key] !== undefined &&
        !definitions.some(
          (field) =>
            field.key === key &&
            ownerForCustomDefinition(field) === 'quote-line' &&
            isValidCustomFieldValue(field, nextValues[key]),
        )
      )
        throw new Error('企业字段值或归属无效，未保存字段值');
      if (
        !sameRawFact(previousValues[key], nextValues[key]) &&
        !canWriteGovernedQuoteField(
          role,
          current,
          key,
          definitions,
          permissionContext,
          authorize,
        )
      )
        throw new Error('字段编辑权限不足，未保存字段值');
    }
  }
}

export function mergeRawQuoteFieldKeys(
  rawKeys: string[],
  selectedKeys: string[],
  role: CpqRole,
  quote: Quote,
  definitions: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  authorize?: QuoteFieldAuthorization,
): string[] {
  return [
    ...rawKeys.filter(
      (key) =>
        !canSelectGovernedQuoteField(
          role,
          quote,
          key,
          definitions,
          permissionContext,
          authorize,
        ),
    ),
    ...selectedKeys,
  ].filter((key, index, all) => all.indexOf(key) === index);
}

export function mergeRawQuoteColumnSnapshot(
  rawSnapshot: QuoteColumnSnapshot[] | undefined,
  selectedSnapshot: QuoteColumnSnapshot[],
  role: CpqRole,
  quote: Quote,
  definitions: QuoteFieldDefinitionRecord[],
  permissionContext?: DisposableFieldPermissionContext,
  authorize?: QuoteFieldAuthorization,
): QuoteColumnSnapshot[] {
  const seen = new Set<string>();
  const protectedRaw = (rawSnapshot ?? []).filter(
    (field) =>
      !canSelectGovernedQuoteField(
        role,
        quote,
        field.key,
        definitions,
        permissionContext,
        authorize,
      ),
  );
  return [...protectedRaw, ...selectedSnapshot].filter((field) => {
    if (seen.has(field.key)) return false;
    seen.add(field.key);
    return true;
  });
}

export function catalogFieldPolicyForKey(
  key: keyof DeviceCatalogRecord,
): QuoteFieldPolicy | undefined {
  return CATALOG_FIELD_POLICY_BY_KEY[key];
}

export function assertCatalogDeviceWriteAllowed(
  state: CpqState,
  original: DeviceCatalogRecord | undefined,
  next: DeviceCatalogRecord,
): void {
  const roleId = activeRoleIdForState(state);
  const mutableFields: Array<keyof DeviceCatalogRecord> = [
    'name',
    'sku',
    'jkyCode',
    'category',
    'procurementAttribute',
    'weight',
    'sortOrder',
    'status',
    'remark',
    'rentalPrice',
    'taxInclusivePlanPrice',
    'unitPrice',
    'retailPriceTax13',
    'dynamicCost',
    'compensationPrice',
    'image',
    'gallery',
    'images',
    'primaryImageId',
    'replacementIds',
    'stockByWarehouse',
  ];
  for (const key of mutableFields) {
    if (original && JSON.stringify(original[key]) === JSON.stringify(next[key]))
      continue;
    if (
      !roleId ||
      !resolveDisposableFieldGrant(
        state,
        roleId,
        permissionFieldIdForDeviceProperty(String(key)),
        'edit',
        'draft',
        activeActorPermissionContext(state),
      )
    ) {
      throw new Error('字段权限不足，未保存设备敏感字段');
    }
  }
}
